/**
 * The file table, shared by intake's client library, the audit's Data tab and
 * step 2 of the audit stepper. The columns and filters live here once; each
 * screen supplies only what differs: where a mapping link points, and whether
 * rows can be selected.
 *
 * "Declared as" is what the uploader said; the classification counts are what
 * the rows turned out to be. Where they disagree the row says so rather than
 * quietly correcting one into the other.
 *
 * Adapted from `reconcolio_dummy_ui_1_9_2026/frontend/src/features/files/FileTable.tsx`.
 */

import { Link } from 'react-router-dom';
import { followPresigned } from '../../api/download';
import { StatusBadge } from '../../shared/ui';
import { useMutation } from '@tanstack/react-query';
import { bytes, count, date } from '../../utils/format';
import {
	needsMapping,
	needsProcessAgain,
	needsProcessing,
} from '../../utils/files';
import { ProcessAgain } from '../intake/ProcessAgain';
import type { Column } from '../shared/tables';
import type { FileRecord } from '../../schemas';
import { Button } from '@mui/material';

/**
 * Fetch a short-lived presigned download URL and follow it. Asked for fresh on
 * every click, never cached: the URL expires.
 */
export async function downloadFile(fileId: string): Promise<void> {
	return followPresigned(`/files/${fileId}/download`);
}

/**
 * The "Download" cell. Its own mutation per row: a shared one would grey out
 * every other row's button while any single file downloads.
 */
function DownloadButton({ file }: { file: FileRecord }) {
	const download = useMutation({ mutationFn: downloadFile });
	return (
		<Button
			loading={download.isPending}
			variant="outlined"
			size="small"

			disabled={download.isPending}
			title={
				download.error
					? download.error instanceof Error
						? download.error.message
						: 'The download failed.'
					: undefined
			}
			onClick={(event) => {
				/* The row itself opens the file, so downloading must not navigate. */
				event.stopPropagation();
				download.mutate(file.pkFileId);
			}}
		>
			Download original file
		</Button>
	);
}

/* ═══ columns ══════════════════════════════════════════════════════════════ */

export interface SelectionOptions {
	selected: Set<string>;
	toggle: (fileId: string) => void;
	/** Why this file cannot be picked, or null when it can. */
	blocked: (file: FileRecord) => string | null;
	/** Tick or clear every pickable row at once. Given, the header cell becomes
	 *  a checkbox. */
	toggleAll?: (on: boolean) => void;
	/** True when every pickable row is already ticked. */
	allSelected?: boolean;
	/** True when some but not all are — the indeterminate dash. */
	someSelected?: boolean;
}

export interface FileColumnOptions {
	/** Open the file — a page on the audit side, a drawer in the picker. */
	onOpen: (file: FileRecord) => void;
	/** Where "Map columns" goes, or null to omit the link entirely. */
	mappingHref?: ((file: FileRecord) => string) | null;
	/** Where "Review rules →" goes for a `MAPPING_CONFIRMED` file, or null to
	 *  omit the link. Same shape as `mappingHref`; callers outside intake pass
	 *  nothing. */
	reviewHref?: ((file: FileRecord) => string) | null;
	/** Present on the stepper's step 2 only. */
	selection?: SelectionOptions;
	/** Add a download column at the end of the row. Off by default: in a picker
	 *  a second button competes with the checkbox. */
	download?: boolean;
	/** Show the status column. Off in a picker, where every offered file is
	 *  READY anyway — see the filter in CreateAudit. */
	status?: boolean;
}

export function fileColumns({
	onOpen,
	mappingHref,
	reviewHref,
	selection,
	download: withDownload,
	status: withStatus = true,
}: FileColumnOptions): Array<Column<FileRecord>> {
	const columns: Array<Column<FileRecord>> = [];

	if (selection) {
		columns.push({
			key: 'pick',
			label: selection.toggleAll ? (
				<input
					type="checkbox"
					aria-label="Select every usable file"
					checked={Boolean(selection.allSelected)}
					ref={(node) => {
						/* The dash for a partial selection. */
						if (node)
							node.indeterminate = Boolean(
								selection.someSelected,
							);
					}}
					onClick={(event) => event.stopPropagation()}
					onChange={(event) =>
						selection.toggleAll?.(event.target.checked)
					}
				/>
			) : (
				''
			),
			width: 34,
			render: (file) => {
				const why = selection.blocked(file);
				return (
					<input
						type="checkbox"
						aria-label={`Include ${file.originalName}`}
						checked={selection.selected.has(file.pkFileId)}
						disabled={Boolean(why)}
						/* Disabled rather than hidden, with the reason on the title. */
						title={why ?? undefined}
						onClick={(event) => event.stopPropagation()}
						onChange={() => selection.toggle(file.pkFileId)}
					/>
				);
			},
		});
	}

	columns.push(
		{
			key: 'name',
			label: 'File',
			width: 210,
			sortValue: (file) => file.originalName,
			render: (file) => (
				<button
					type="button"
					className="step-open trunc"
					title={`${file.originalName} · ${bytes(file.sizeBytes)} · uploaded ${date(file.createdAt)}`}
					onClick={(event) => {
						event.stopPropagation();
						onOpen(file);
					}}
				>
					{file.originalName}
				</button>
			),
		},
		{
			key: 'period',
			label: 'Period',
			width: 96,
			sortValue: (file) => file.periodStart ?? '',
			render: (file) =>
				file.periodLabel ? (
					<span
						className="nowrap"
						title={
							file.periodStart
								? `${date(file.periodStart)} – ${date(file.periodEnd ?? file.periodStart)}`
								: undefined
						}
					>
						{file.periodLabel}
					</span>
				) : (
					<span className="mut">—</span>
				),
		},
		{
			key: 'rows',
			label: 'Rows',
			num: true,
			width: 90,
			sortValue: (file) => file.rowCount ?? -1,
			/* "not read" and "0" are different answers; never print one for the other. */
			render: (file) =>
				file.rowCount === null ? (
					<span className="mut">Not read yet</span>
				) : file.rowCount > 0 ? (
					count(file.rowCount)
				) : (
					<span className="mut">0</span>
				),
		},
		/* Layout, the classification split and the claim-vs-content verdict are in
       the file's drawer, not here: thirteen columns made this table 1,900px
       wide. */
		...(withStatus
			? ([
					{
						key: 'status',
						label: 'Status',
						width: 132,
						sortValue: (file: FileRecord) => file.status,
						/* The badge and, where there is one, the action. The status note
               is a sentence, so it stays on the title. */
						render: (file: FileRecord) => (
							<div
								className="fstat"
								title={file.statusNote ?? undefined}
							>
								<StatusBadge
									group="fileStatus"
									value={file.status}
								/>
								{needsMapping(file) && mappingHref && (
									<Link
										to={mappingHref(file)}
										className="link small nowrap"
										onClick={(event) =>
											event.stopPropagation()
										}
									>
										Map columns
									</Link>
								)}
								{needsProcessing(file) && reviewHref && (
									<Link
										to={reviewHref(file)}
										className="link small nowrap"
										onClick={(event) =>
											event.stopPropagation()
										}
									>
										Review rules
									</Link>
								)}
								{needsProcessAgain(file) && reviewHref && (
									<ProcessAgain file={file} />
								)}
							</div>
						),
					},
				] as Array<Column<FileRecord>>)
			: []),
	);

	if (withDownload) {
		columns.push({
			key: 'download',
			label: '',
			width: 200,
			render: (file) => <DownloadButton file={file} />,
		});
	}

	return columns;
}
